<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <div class="bg-gradient-to-r from-primary-800 to-primary-600 text-white py-12">
      <div class="container mx-auto px-4">
        <h1 class="text-4xl md:text-5xl font-bold mb-6 text-white">ReacTCR</h1>
        <p class="text-xl md:text-2xl mb-8 text-white">Reactive TCR-pMHC Atlas</p>
        <div class="flex flex-col md:flex-row gap-4">
          <button
            @click="navigateTo('/search')"
            class="bg-white text-primary-800 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center"
          >
            <i class="fas fa-search mr-2"></i>Start Searching
          </button>
          <button
            @click="navigateTo('/browse')"
            class="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-800 transition-colors text-center"
          >
            <i class="fas fa-download mr-2"></i>Start Browsing
          </button>
        </div>
      </div>
    </div>

    <!-- Statistics Section -->
    <div class="bg-gray-50 py-8">
      <div class="container mx-auto px-4">
        <h2 class="text-3xl font-bold text-center mb-12 text-gray-800">Database Statistics</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div class="bg-white p-6 rounded-lg shadow-md">
            <div class="text-3xl font-bold text-primary-800 mb-2">{{ databaseStats.totalEntries }}</div>
            <div class="text-gray-600">Overall Entries</div>
          </div>
          <div class="bg-white p-6 rounded-lg shadow-md">
            <div class="text-3xl font-bold text-primary-800 mb-2">{{ databaseStats.tcrCount }}</div>
            <div class="text-gray-600">TCR Sequences</div>
          </div>
          <div class="bg-white p-6 rounded-lg shadow-md">
            <div class="text-3xl font-bold text-primary-800 mb-2">{{ databaseStats.functionalPairs }}</div>
            <div class="text-gray-600">Reactive TCR-pMHC pairs</div>
          </div>
          <div class="bg-white p-6 rounded-lg shadow-md">
            <div class="text-3xl font-bold text-primary-800 mb-2">{{ databaseStats.pdbStructures }}</div>
            <div class="text-gray-600">Published Structures</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="container mx-auto px-4 py-8">
      <h2 class="text-3xl font-bold text-center mb-12 text-gray-800">Key Features</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div class="text-primary-800 mb-4">
            <i class="fas fa-search text-4xl"></i>
          </div>
          <h3 class="text-xl font-semibold mb-2">Data Search</h3>
          <p class="text-gray-600">Quickly search TCR sequences, pMHCs, and TCR-pMHC interactions</p>
        </div>

        <div class="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div class="text-primary-800 mb-4">
            <i class="fas fa-download text-4xl"></i>
          </div>
          <h3 class="text-xl font-semibold mb-2">Data Download</h3>
          <p class="text-gray-600">Download complete TCR, pMHCs, and TCR-pMHC interactions</p>
        </div>

        <div class="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div class="text-primary-800 mb-4">
            <i class="fas fa-bolt text-4xl"></i>
          </div>
          <h3 class="text-xl font-semibold mb-2">Quick Analysis</h3>
          <p class="text-gray-600">Analyze TCR-pMHC interactions</p>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="container mx-auto px-4 py-8">
      <h2 class="text-3xl font-bold text-center mb-12 text-gray-800">Distribution</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <h3 class="text-xl font-semibold mb-4 text-gray-800 text-center">TCR sequence length</h3>
          <div class="flex justify-center space-x-4 mb-4">
            <button
              @click="updateChartType('group')"
              :class="[
                'px-4 py-2 rounded-lg transition-colors',
                chartType === 'group' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              Grouped
            </button>
            <button
              @click="updateChartType('stack')"
              :class="[
                'px-4 py-2 rounded-lg transition-colors',
                chartType === 'stack' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              Stacked
            </button>
          </div>
          <div ref="tcrLengthChartRef" class="w-full h-[400px]"></div>
        </div>
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <h3 class="text-xl font-semibold mb-4 text-gray-800 text-center">#TCR in Target</h3>
          <div ref="targetDistributionChartRef" class="w-full h-[400px]"></div>
        </div>
      </div>
    </div>

    <!-- Updates and Publications Section -->
    <div class="container mx-auto px-4 py-8">
      <h2 class="text-3xl font-bold text-center mb-12 text-gray-800">Updates & Publications</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <!-- Update Log -->
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <h3 class="text-xl font-semibold mb-4 text-gray-800">News & updates</h3>
          <div class="space-y-4 h-[330px] overflow-y-auto pr-2">
            <div v-for="(update, index) in updateLogs" :key="index" class="border-l-4 border-primary-700 pl-4">
              <div class="text-sm text-gray-500">{{ update.date }}</div>
              <div class="text-gray-700">{{ update.content }}</div>
            </div>
          </div>
        </div>

        <!-- Publication Statistics -->
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <h3 class="text-xl font-semibold mb-4 text-gray-800 text-center">Records of TCRs by year</h3>
          <div ref="numberoftcrChartRef" class="w-full h-[350px]"></div>
        </div>
        <!-- Publication Statistics -->
        <div class="bg-white p-6 rounded-lg shadow-lg">
          <h3 class="text-xl font-semibold mb-4 text-gray-800 text-center">Records of Epitopes by year</h3>
          <div ref="epitopeChartRef" class="w-full h-[350px]"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import * as echarts from 'echarts'
import { chartTheme, getPieChartColor, getBarChartStyle, getLineChartStyle } from '@/styles/chart-theme'
import { tcrApi } from '@/api/tcr'
// import TreeChart from '@/components/TreeChart.vue'
// import MoleculeAtlas from '@/components/MoleculeAtlas.vue'

const router = useRouter()
const tcrLengthChartRef = ref<HTMLElement | null>(null)
const targetDistributionChartRef = ref<HTMLElement | null>(null)
const epitopeChartRef = ref<HTMLElement | null>(null)
const numberoftcrChartRef = ref<HTMLElement | null>(null)

// 图表实例引用
const tcrChartInstance = ref<echarts.ECharts | null>(null)
const targetChartInstance = ref<echarts.ECharts | null>(null)
const epitopeChartInstance = ref<echarts.ECharts | null>(null)
const numberoftcrChartInstance = ref<echarts.ECharts | null>(null)

const tcrLengthData = ref({
  lengths: [] as number[],
  travCounts: [] as number[],
  trbvCounts: [] as number[]
})

// 添加图表类型切换
const chartType = ref<'group' | 'stack'>('group')

const targetDistributionData = ref({
  genes: [] as string[],
  counts: [] as number[]
})

// 更新日志数据
const updateLogs = [
  {
    date: '2025-04-30',
    content: 'Version 1.0 released'
  },
  {
    date: '2024-04-18',
    content: 'Update database structure, optimize search performance'
  },
  {
    date: '2025-04-15',
    content: 'Release database beta version'
  }
]

const databaseStats = ref({
  totalEntries: 0,
  tcrCount: 0,
  functionalPairs: 0,
  pdbStructures: 0
})

const numberoftcrData = ref({
  years: [] as number[],
  counts: [] as number[],
  cumulativeCounts: [] as number[]
})

const epitopeData = ref({
  years: [] as number[],
  counts: [] as number[],
  cumulativeCounts: [] as number[]
})

// 使用主题颜色
const textColor = chartTheme.text.primary
const backgroundColor = chartTheme.background.light

// 使用辅助函数获取图表样式
const barChartStyle = getBarChartStyle()
const lineChartStyle = getLineChartStyle('primary')

// 树状图数据
// const tcrTreeData = {
//   name: 'TCR-pMHC',
//   children: [
//     {
//       name: 'MHC Class I',
//       children: [
//         {
//           name: 'HLA-A',
//           children: [
//             { name: 'A*02:01', value: 120 },
//             { name: 'A*01:01', value: 80 },
//             { name: 'A*03:01', value: 60 }
//           ]
//         },
//         {
//           name: 'HLA-B',
//           children: [
//             { name: 'B*07:02', value: 90 },
//             { name: 'B*08:01', value: 70 }
//           ]
//         }
//       ]
//     },
//     {
//       name: 'MHC Class II',
//       children: [
//         {
//           name: 'HLA-DR',
//           children: [
//             { name: 'DRB1*01:01', value: 50 },
//             { name: 'DRB1*04:01', value: 40 }
//           ]
//         },
//         {
//           name: 'HLA-DQ',
//           children: [
//             { name: 'DQB1*02:01', value: 30 },
//             { name: 'DQB1*03:01', value: 20 }
//           ]
//         }
//       ]
//     }
//   ]
// }

// 分子网络数据
// const moleculeData = {
//   nodes: [
//     { id: 'tretinoin', name: 'Tretinoin', category: 'DOT' as const, symbolSize: 50 },
//     { id: 'ibuprofen', name: 'Ibuprofen', category: 'DOT' as const, symbolSize: 45 },
//     { id: 'nicotine', name: 'Nicotine', category: 'DOT' as const, symbolSize: 45 },
//     { id: 'curcumin', name: 'Curcumin', category: 'DOT' as const, symbolSize: 45 },
//     { id: 'doxorubicin', name: 'Doxorubicin', category: 'DOT' as const, symbolSize: 45 },
//     { id: 'cannabidiol', name: 'Cannabidiol', category: 'DOT' as const, symbolSize: 45 },
//     { id: 'methotrexate', name: 'Methotrexate', category: 'DOT' as const, symbolSize: 45 },
//     { id: 'phenytoin', name: 'Phenytoin', category: 'DOT' as const, symbolSize: 45 },
//     { id: 'phenobarbital', name: 'Phenobarbital', category: 'DOT' as const, symbolSize: 45 },
//     { id: 'TRPA1', name: 'TRPA1', category: 'DTT' as const, symbolSize: 35 },
//     { id: 'HMGCR', name: 'HMGCR', category: 'DTT' as const, symbolSize: 35 },
//     { id: 'CA12', name: 'CA12', category: 'DTT' as const, symbolSize: 35 },
//     { id: 'PTGS2', name: 'PTGS2', category: 'DTT' as const, symbolSize: 35 }
//   ],
//   links: [
//     { source: 'tretinoin', target: 'TRPA1' },
//     { source: 'ibuprofen', target: 'PTGS2' },
//     { source: 'nicotine', target: 'TRPA1' },
//     { source: 'curcumin', target: 'HMGCR' },
//     { source: 'doxorubicin', target: 'CA12' },
//     { source: 'cannabidiol', target: 'PTGS2' },
//     { source: 'methotrexate', target: 'HMGCR' },
//     { source: 'phenytoin', target: 'CA12' },
//     { source: 'phenobarbital', target: 'TRPA1' },
//     { source: 'tretinoin', target: 'HMGCR' },
//     { source: 'ibuprofen', target: 'CA12' },
//     { source: 'nicotine', target: 'PTGS2' }
//   ]
// }


const initCharts = async () => {
  await nextTick()
  
  if (!tcrLengthChartRef.value || !targetDistributionChartRef.value || !epitopeChartRef.value || !numberoftcrChartRef.value) {
    return
  }

  // 如果实例已存在，先销毁
  if (tcrChartInstance.value) tcrChartInstance.value.dispose()
  if (targetChartInstance.value) targetChartInstance.value.dispose()
  if (epitopeChartInstance.value) epitopeChartInstance.value.dispose()
  if (numberoftcrChartInstance.value) numberoftcrChartInstance.value.dispose()

  // 初始化TCR长度分布图
  tcrChartInstance.value = echarts.init(tcrLengthChartRef.value)
  tcrChartInstance.value.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    legend: {
      data: ['TRAV', 'TRBV'],
      textStyle: {
        color: textColor
      }
    },
    grid: {
      left: '5%',
      right: '4%',
      bottom: '15%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: tcrLengthData.value.lengths,
      name: 'Length',
      nameLocation: 'center',
      nameGap: 30,
      axisLabel: {
        color: textColor
      }
    },
    yAxis: {
      type: 'value',
      name: 'Count',
      nameLocation: 'center',
      nameGap: 35,
      axisLabel: {
        color: textColor
      }
    },
    series: [
      {
        name: 'TRAV',
        data: tcrLengthData.value.travCounts,
        type: 'bar',
        showBackground: true,
        backgroundStyle: {
          color: backgroundColor
        },
        itemStyle: {
          color: chartTheme.chart.bar.primary.color
        },
        emphasis: {
          itemStyle: {
            color: chartTheme.chart.bar.primary.emphasis
          }
        }
      },
      {
        name: 'TRBV',
        data: tcrLengthData.value.trbvCounts,
        type: 'bar',
        showBackground: true,
        backgroundStyle: {
          color: backgroundColor
        },
        itemStyle: {
          color: chartTheme.chart.bar.secondary.color
        },
        emphasis: {
          itemStyle: {
            color: chartTheme.chart.bar.secondary.emphasis
          }
        }
      }
    ]
  })

  // 初始化靶点分布图
  targetChartInstance.value = echarts.init(targetDistributionChartRef.value)
  targetChartInstance.value.setOption({
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: 'horizontal',
      left: 'left',
      textStyle: {
        color: textColor
      },
      itemWidth:15,
      itemGap:10,
      type:'scroll',
      pageIconColor: textColor
    },
    series: [{
      type: 'pie',
      radius: '50%',
      data: targetDistributionData.value.genes.map((gene, index) => ({
        name: gene,
        value: targetDistributionData.value.counts[index],
        itemStyle: {
          color: getPieChartColor(index)
        }
      })),
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }]
  })

  // 初始化TCR数量统计图
  numberoftcrChartInstance.value = echarts.init(numberoftcrChartRef.value)
  numberoftcrChartInstance.value.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: ['Annual Count', 'Cumulative Count'],
      textStyle: {
        color: textColor
      }
    },
    grid: {
      left: '6%',
      right: '3%',
      bottom: '10%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: numberoftcrData.value.years,
      name: 'Year',
      nameLocation: 'center',
      nameGap: 30,
      axisLabel: {
        color: textColor,
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of TCRs',
      nameLocation: 'center',
      nameGap: 45,
      axisLabel: {
        color: textColor,
      }
    },
    series: [
      {
        name: 'Annual Count',
        data: numberoftcrData.value.counts,
        type: 'bar',
        itemStyle: {
          color: '#0168a2'
        }
      },
      {
        name: 'Cumulative Count',
        data: numberoftcrData.value.cumulativeCounts,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          width: 3,
          color: '#0168a2'
        },
        itemStyle: {
          color: '#0168a2'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(1, 104, 162, 0.8)' },
            { offset: 1, color: 'rgba(1, 104, 162, 0.1)' }
          ])
        }
      }
    ]
  })

  // 初始化文献发表统计图
  epitopeChartInstance.value = echarts.init(epitopeChartRef.value)
  epitopeChartInstance.value.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: ['Annual Count', 'Cumulative Count'],
      textStyle: {
        color: textColor
      }
    },
    grid: {
      left: '6%',
      right: '3%',
      bottom: '10%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: epitopeData.value.years,
      name: 'Year',
      nameLocation: 'center',
      nameGap: 30,
      axisLabel: {
        color: textColor
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Epitopes',
      nameLocation: 'center',
      nameGap: 45,
      axisLabel: {
        color: textColor,
      }
    },
    series: [
      {
        name: 'Annual Count',
        data: epitopeData.value.counts,
        type: 'bar',
        itemStyle: {
          color: '#166534'
        }
      },
      {
        name: 'Cumulative Count',
        data: epitopeData.value.cumulativeCounts,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          width: 3,
          color: '#166534'
        },
        itemStyle: {
          color: '#166534'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(22, 101, 52, 0.8)' }, //31, 153, 112, 0.1
            { offset: 1, color: 'rgba(22, 101, 52, 0.1)' }
          ])
        }
      }
    ]
  })
}

const navigateTo = async (path: string) => {
  try {
    console.log('Navigating to:', path)
    await router.push(path)
    console.log('Navigation successful')
  } catch (error) {
    console.error('Navigation failed:', error)
  }
}

// 获取TCR序列长度统计
const fetchTCRLengthStats = async () => {
  try {
    const data = await tcrApi.getSequenceLengthStats()
    
    if (!data || !data.trav || !data.trbv) {
      console.error('Invalid TCR length statistics data:', data)
      return
    }
    
    // 获取所有可能的长度值
    const allLengths = [...new Set([...data.trav.lengths, ...data.trbv.lengths])].sort((a, b) => a - b)
    
    // 为每个长度创建计数数组
    const travCounts = allLengths.map(length => {
      const index = data.trav.lengths.indexOf(length)
      return index !== -1 ? data.trav.counts[index] : 0
    })
    
    const trbvCounts = allLengths.map(length => {
      const index = data.trbv.lengths.indexOf(length)
      return index !== -1 ? data.trbv.counts[index] : 0
    })
    
    tcrLengthData.value = {
      lengths: allLengths,
      travCounts,
      trbvCounts
    }
    
    // 更新图表
    if (tcrChartInstance.value) {
      tcrChartInstance.value.setOption({
        xAxis: {
          data: tcrLengthData.value.lengths
        },
        series: [
          {
            name: 'TRAV',
            data: tcrLengthData.value.travCounts
          },
          {
            name: 'TRBV',
            data: tcrLengthData.value.trbvCounts
          }
        ]
      })
    }
  } catch (error) {
    console.error('Error fetching TCR length statistics:', error)
  }
}

// 更新图表类型
const updateChartType = (type: 'group' | 'stack') => {
  chartType.value = type
  if (tcrChartInstance.value) {
    tcrChartInstance.value.setOption({
      series: [
        {
          name: 'TRAV',
          stack: type === 'stack' ? 'total' : undefined,
          data: tcrLengthData.value.travCounts
        },
        {
          name: 'TRBV',
          stack: type === 'stack' ? 'total' : undefined,
          data: tcrLengthData.value.trbvCounts
        }
      ]
    })
  }
}

// 获取抗原基因统计
const fetchEpitopeGeneStats = async () => {
  try {
    const data = await tcrApi.getEpitopeGeneStats()
    
    if (!data || !data.genes || !data.counts) {
      console.error('Invalid epitope gene statistics data:', data)
      return
    }
    
    targetDistributionData.value = {
      genes: data.genes,
      counts: data.counts
    }
    
    // 更新图表
    if (targetChartInstance.value) {
      targetChartInstance.value.setOption({
        series: [{
          data: targetDistributionData.value.genes.map((gene, index) => ({
            name: gene,
            value: targetDistributionData.value.counts[index],
            itemStyle: {
              color: getPieChartColor(index)
            }
          }))
        }]
      })
    }
  } catch (error) {
    console.error('Error fetching epitope gene statistics:', error)
  }
}

// 获取数据库统计信息
const fetchDatabaseStats = async () => {
  try {
    console.log('Starting fetchDatabaseStats...')
    const response = await tcrApi.getDatabaseStats()
    console.log('Database stats API response:', response)
    
    if (!response) {
      console.error('No response received from database stats API')
      return
    }
    
    // 更严格的类型检查
    if (typeof response !== 'object' || response === null) {
      console.error('Invalid response type:', typeof response)
      return
    }
    
    // 检查所有必需字段
    const requiredFields = ['total_entries', 'tcr_count', 'functional_pairs', 'pdb_structures']
    const missingFields = requiredFields.filter(field => !(field in response))
    if (missingFields.length > 0) {
      console.error('Missing required fields in response:', missingFields)
      return
    }
    
    // 确保数值类型正确
    const numericFields = ['total_entries', 'tcr_count', 'functional_pairs', 'pdb_structures']
    for (const field of numericFields) {
      if (typeof response[field] !== 'number' || isNaN(response[field])) {
        console.error(`Invalid value for ${field}:`, response[field])
        return
      }
    }
    
    databaseStats.value = {
      totalEntries: response.total_entries,
      tcrCount: response.tcr_count,
      functionalPairs: response.functional_pairs,
      pdbStructures: response.pdb_structures
    }
    
    console.log('Updated database stats:', databaseStats.value)
  } catch (error) {
    console.error('Error in fetchDatabaseStats:', error)
    if (error instanceof Error) {
      console.error('Error details:', error.message)
    }
  }
}

// 获取TCR时间线统计
const fetchTCRTimelineStats = async () => {
  try {
    const data = await tcrApi.getTCRTimelineStats()
    
    if (!data || !data.years || !data.counts) {
      console.error('Invalid TCR timeline statistics data:', data)
      return
    }
    
    let cumulativeSum = 0
    const cumulativeCounts = data.counts.map((count: number) => {
      cumulativeSum += count
      return cumulativeSum
    })
    
    numberoftcrData.value = {
      years: data.years,
      counts: data.counts,
      cumulativeCounts
    }
    
    if (numberoftcrChartInstance.value) {
      numberoftcrChartInstance.value.setOption({
        xAxis: {
          data: numberoftcrData.value.years
        },
        series: [
          {
            data: numberoftcrData.value.counts
          },
          {
            data: numberoftcrData.value.cumulativeCounts
          }
        ]
      })
    }
  } catch (error) {
    console.error('Error fetching TCR timeline statistics:', error)
  }
}

// 获取文献发表统计
const fetchEpitopeTimelineStats = async () => {
  try {
    const data = await tcrApi.getEpitopeTimelineStats()
    
    if (!data || !data.years || !data.counts) {
      console.error('Invalid epitope timeline statistics data:', data)
      return
    }
    
    let cumulativeSum = 0
    const cumulativeCounts = data.counts.map((count: number) => {
      cumulativeSum += count
      return cumulativeSum
    })
    
    epitopeData.value = {
      years: data.years,
      counts: data.counts,
      cumulativeCounts
    }
    
    if (epitopeChartInstance.value) {
      epitopeChartInstance.value.setOption({
        xAxis: {
          data: epitopeData.value.years
        },
        series: [
          {
            data: epitopeData.value.counts
          },
          {
            data: epitopeData.value.cumulativeCounts
          }
        ]
      })
    }
  } catch (error) {
    console.error('Error fetching publication statistics:', error)
  }
}

onMounted(async () => {
  await initCharts()
  await fetchTCRLengthStats()
  await fetchEpitopeGeneStats()
  await fetchDatabaseStats()
  await fetchTCRTimelineStats()
  await fetchEpitopeTimelineStats()
})

onUnmounted(() => {
  if (tcrChartInstance.value) tcrChartInstance.value.dispose()
  if (targetChartInstance.value) targetChartInstance.value.dispose()
  if (epitopeChartInstance.value) epitopeChartInstance.value.dispose()
  if (numberoftcrChartInstance.value) numberoftcrChartInstance.value.dispose()
  window.removeEventListener('resize', () => {
    tcrChartInstance.value?.resize()
    targetChartInstance.value?.resize()
    epitopeChartInstance.value?.resize()
    numberoftcrChartInstance.value?.resize()
  })
})
</script>

<style scoped>
.container {
  max-width: 1280px;
  margin: 0 auto;
}

/* 确保图表容器有明确的尺寸 */
.w-full {
  width: 100%;
}

.h-\[400px\] {
  height: 400px;
  min-height: 400px;
}

.h-\[350px\] {
  height: 350px;
  min-height: 350px;
}

/* Add smooth transitions */
.transition-shadow {
  transition: box-shadow 0.3s ease;
}

/* Add hover effects */
.hover\:shadow-xl:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
</style>